import { useQuery } from '@tanstack/react-query'
import { api } from './client'
import { hydratePractitionerTypes, needsVocabularyFetch } from '@/lib/practitioner-types'

/**
 * Server-served vocabularies (CLA-1148, CLA-1149).
 *
 * These lists live in the backend modules that validate writes. They were
 * mirrored by hand in this client — 31 specialties typed out here, still
 * commented as matching a Python constant deleted in July — which meant a
 * specialty added on the server could not be picked until the SPA shipped, and
 * that the client and the validator could disagree without anyone noticing.
 *
 * A long `staleTime`: these change when someone edits the catalogue, which is
 * rare, and refetching a fixed list on every mount buys nothing.
 */

const HOUR = 60 * 60 * 1000

export interface MedicalSpecialty {
  key: string
  label: string
  /** Clinical types shown prominently for this specialty — a hint, not a restriction. */
  primary_types: string[]
}

export interface SpecialtyGroup {
  label: string
  values: string[]
}

export interface MedicalSpecialtyVocabulary {
  specialties: MedicalSpecialty[]
  /**
   * Picker groups (CLA-1145). Server-owned for the same reason the
   * practitioner-type groups are — a client must not invent its own names for a
   * clinical grouping — and needed because 43 specialties rendered flat is
   * unscannable.
   *
   * Optional: an older server, or a response cached before this shipped, has no
   * `groups`, and the picker falls back to the flat list rather than rendering
   * nothing.
   */
  groups?: SpecialtyGroup[]
}

const specialtyQuery = {
  queryKey: ['medical-specialties'] as const,
  queryFn: async () => api.get<MedicalSpecialtyVocabulary>('/medical-specialties'),
  staleTime: HOUR,
  // A picker with nothing in it is indistinguishable from a picker whose
  // options were all removed, so callers render a loading state instead —
  // there is deliberately no hardcoded fallback list to drift from.
  retry: 1,
}

/** The flat specialty list, in catalogue order. */
export function useMedicalSpecialties() {
  return useQuery({
    ...specialtyQuery,
    select: (v: MedicalSpecialtyVocabulary) => v.specialties,
  })
}

/**
 * The specialty list plus its picker groups.
 *
 * Same query key as `useMedicalSpecialties`, so React Query dedupes them to one
 * request — this is a different view of the same response, not a second fetch.
 */
export function useMedicalSpecialtyVocabulary() {
  return useQuery(specialtyQuery)
}

export interface PractitionerTypeVocabulary {
  options: { value: string; label: string; registration: 'ahpra' | 'none' | 'unknown' }[]
  groups: { label: string; values: string[] }[]
}

/**
 * Adopt the served practitioner-type vocabulary (CLA-1148).
 *
 * Unlike the specialty list, this one is read SYNCHRONOUSLY all over the app —
 * admin and manage screens label other users' stored roles while rendering
 * lists — so it cannot become an async hook without a wide refactor and a lot
 * of label flicker. Instead `lib/practitioner-types` keeps a built-in copy of
 * the backend seed as a fallback and this replaces it once, which is the same
 * shape `lib/jurisdiction.ts` already uses.
 *
 * The drift that mattered is gone either way: the server wins, and a
 * profession added there reaches clients without a release.
 */
export function useVocabularyHydration() {
  // Skipped when a stored copy is still inside its TTL (CLA-1170). staleTime
  // alone cannot do this: a cold page load starts with an empty React Query
  // cache, so this refetched on EVERY boot — one of only two requests in the
  // first wave, racing /auth/me before auth had resolved — to re-learn a list
  // of professions that changes approximately never. The stored copy is
  // already in effect by then; `lib/practitioner-types` reads it at module
  // load. `enabled` is read once on mount, which is what we want: a boot
  // decision, not something that flips mid-session.
  useQuery({
    queryKey: ['practitioner-types'],
    queryFn: async () => {
      const res = await api.get<PractitionerTypeVocabulary>('/practitioner-types')
      hydratePractitionerTypes(res.options)
      return res
    },
    staleTime: HOUR,
    retry: 1,
    enabled: needsVocabularyFetch(),
  })
}
